import React from 'react';
import { Collapse, Row, Col } from 'react-bootstrap';
import { FaClock, FaCheckCircle, FaCheck, FaTimesCircle } from 'react-icons/fa';
import { Text, Fallback } from 'v3/components';
import * as S from '../styles';

function getStatusMeta(item) {
  const isPending = item.status === 'pending';
  const isCanceled = item.status === 'canceled';

  if (isPending) {
    return {
      pillText: 'Pendente',
      timeStyle: S.timeChipWarn,
      pillStyle: S.statusPillWarn,
      labelColor: '#fb8c00',
      icon: <FaClock size={12} />,
    };
  }

  if (isCanceled) {
    return {
      pillText: 'Anulada',
      timeStyle: S.timeChipDanger,
      pillStyle: S.statusPillDanger,
      labelColor: '#c62828',
      icon: <FaTimesCircle size={12} />,
    };
  }

  return {
    pillText: 'Concluído',
    timeStyle: S.timeChipOk,
    pillStyle: S.statusPillOk,
    labelColor: '#2e7d32',
    icon: <FaCheckCircle size={12} />,
  };
}

function StatusInsideTimeChip({ item, elapsed }) {
  const { timeStyle, icon, pillText, labelColor } = getStatusMeta(item);
  if (item?.status === 'canceled') {
    return (
      <span style={{ ...S.timeChip, ...timeStyle }} title={pillText}>
        {icon}
        <strong style={{ marginLeft: 6, color: labelColor }}>{pillText}</strong>
      </span>
    );
  }
  const timeTxt = elapsed(item.openedAt, item.finishedAt);
  const showTime = timeTxt && timeTxt !== '-' && timeTxt !== '—';

  return (
    <span style={{ ...S.timeChip, ...timeStyle }} title={pillText}>
      {icon}
      {showTime && <span>{timeTxt}</span>}
      {showTime && <span style={{ margin: '0 8px', opacity: 0.4 }}>|</span>}
      <strong style={{ color: labelColor }}>{pillText}</strong>
    </span>
  );
}

function ActionsCell({ item, canFinish, canCancel, resolving, canceling, onClickFinish, onClickCancel }) {
  const showActions = item.status === 'pending' && !resolving && !canceling;
  const disabledFinish = !canFinish || resolving;
  const disabledCancel = !canCancel || canceling;

  if (!showActions) return <div style={S.actionsRow} />;

  return (
    <div style={S.actionsRow}>
      <button
        type="button"
        style={{ ...S.actionPill, ...S.actionPillOk, ...(disabledFinish ? S.actionPillDisabled : {}) }}
        disabled={disabledFinish}
        onClick={(e) => {
          e.stopPropagation();
          if (!canFinish) {
            console.warn('[BLOCK:FINISH] Clique em CONCLUIR bloqueado', {
              adjId: item.id,
              responsavel: item.groupSlug,
            });
            return;
          }
          onClickFinish(item, canFinish);
        }}
      >
        <FaCheck size={12} style={{ marginRight: 6 }} />
        Concluir
      </button>

      <button
        type="button"
        style={{ ...S.actionPill, ...S.actionPillDanger, ...(disabledCancel ? S.actionPillDisabled : {}) }}
        disabled={disabledCancel}
        onClick={(e) => {
          e.stopPropagation();
          if (!canCancel) {
            console.warn('[BLOCK:CANCEL] Clique em ANULAR bloqueado', {
              adjId: item.id,
              responsavel: item.groupSlug,
            });
            return;
          }
          onClickCancel(item, canCancel);
        }}
      >
        <span style={S.cancelGlyph}>Ø</span> Anular
      </button>
    </div>
  );
}

const sameStr = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
const sameId  = (a, b) => {
  const na = Number(a), nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) && na === nb;
};

export default function AdjustmentList({
  loading,
  items,
  expandedId,
  resolving,
  canceling,
  StatusChip,
  userGroupSlugs,
  currentUserId,  
  currentUsername,
  fmtDate,
  fmtDay,
  elapsed,
  onRowClick,
  onClickFinish,
  onClickCancel,
}) {
  return (
    <Fallback fall={loading} component={<div style={{ padding: 24 }}><Text>Carregando…</Text></div>}>
      <div style={S.tableWrap}>
        <table className="table mb-0" style={S.table}>
          <thead style={S.thead}>
            <tr>
              <th className="text-center">Tempo | Status</th>
              <th>Setor</th>
              <th>Criado por</th>
              <th>Abertura</th>
              <th>Motivo</th>
              <th>Descrição</th>
              <th>Finalizado em</th>
              <th>Finalizado por</th>
              <th>Resolução</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const isOpen = expandedId === item.id;
              const canAct = item.status === 'pending';
              const respSlug = String(item.groupSlug || '').toUpperCase();
              const belongs  = userGroupSlugs?.has ? userGroupSlugs.has(respSlug) : false;
              const canFinish = canAct && belongs;
              const isCreator =
                sameId(item.createdById, currentUserId) ||
                sameStr(item.createdBy, currentUsername);

              const canCancel = canAct && (belongs || isCreator);
              return (
                <React.Fragment key={item.id}>
                  <tr
                    style={{ ...(item.status === 'pending' ? S.rowPending : null), ...S.rowHover }}
                    onClick={() => onRowClick(item)}
                  >
                    <td className="text-center align-middle"><StatusInsideTimeChip item={item} elapsed={elapsed} /></td>
                    <td className="align-middle">{item.groupName}</td>
                    <td className="align-middle">{item.createdBy || '-'}</td>
                    <td className="align-middle">{fmtDay(item.openedAt)}</td>
                    <td className="align-middle">{item.reason}</td>
                    <td className="align-middle" style={S.colDesc}>{item.description || '-'}</td>
                    <td className="align-middle">
                     {item.status === 'finished' ? fmtDay(item.finishedAt) : '-'}
                    </td>
                    <td className="align-middle">
                      {item.status === 'finished' ? fmtDay(item.finishedAt) : '-'}
                    </td>
                    <td className="align-middle" style={S.colDesc}>{(item.status === 'finished' || item.status === 'canceled') ? (item.resolution || '-') : '-'}</td>
                    <td className="text-right align-middle" style={{ whiteSpace: 'nowrap' }}>
                      <ActionsCell
                        item={item}
                        canFinish={canFinish}
                        canCancel={canCancel}
                        resolving={resolving}
                        canceling={canceling}
                        onClickFinish={onClickFinish}
                        onClickCancel={onClickCancel}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={10} style={S.collapseCellTd}>
                      <Collapse in={isOpen}>
                        <div>
                          <div style={S.detailsBox}>
                            <div style={S.detailsHeader}>
                              <Text type="regular" color="dark" weight="600">
                                Informações da Pendência
                              </Text>
                              <StatusChip status={item.status} />
                            </div>
                            <Row>
                              <Col md={6}>
                                <div style={S.field}><span style={S.fieldLabel}>Data inclusão:</span> {fmtDate(item.openedAt)}</div>
                                <div style={S.field}><span style={S.fieldLabel}>Incluído por:</span> {item.createdBy || '-'}</div>
                                <div style={S.field}><span style={S.fieldLabel}>Setor de origem:</span> {item.fromGroup?.name}</div>
                                <div style={S.field}><span style={S.fieldLabel}>Setor Responsável:</span> {item.groupName}</div>
                                <div style={S.field}><span style={S.fieldLabel}>Motivo:</span> {item.reason}</div>
                                <div style={S.field}><span style={S.fieldLabel}>Descrição:</span> {item.description || '-'}</div>
                              </Col>
                              <Col md={6}>
                                 <div style={S.field}>
                                   <span style={S.fieldLabel}>Tempo decorrido:</span>{' '}
                                   {item.status === 'canceled' ? '-' : elapsed(item.openedAt, item.finishedAt)}
                                 </div>
                                <div style={S.field}>
                                  <span style={S.fieldLabel}>Finalizado em:</span>
                                  {item.status === 'finished'
                                    ? fmtDate(item.finishedAt)
                                    : '-'}
                                </div>
                                <div style={S.field}>
                                  <span style={S.fieldLabel}>Finalizado por:</span>
                                  {item.status === 'finished'
                                    ? (item.finishedBy || '-')
                                    : item.status === 'canceled'
                                    ? (item.canceledBy || '-')
                                    : '-'}
                                </div>
                                <div style={S.field}>
                                  <span style={S.fieldLabel}>Resolução:</span>
                                  {(item.status === 'finished' || item.status === 'canceled') ? (item.resolution || '-') : '-'}
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={10} className="text-center py-4">
                  <Text>Não há pendências para exibir</Text>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Fallback>
  );
}